import {defineStore, type StoreDefinition} from 'pinia'
import type { AuthData, AuthResponse, AuthResponseData, Group, Permission } from '@/types/Auth'
import type { ApiErrorResponse } from '@/types/api'
import { decryptTokenPacketToBearer, decryptTokenPacketToString } from '@/utils/tokenDecrypt';
import { extractAccessRefresh } from '@/utils/tokenNormalize';
import { PrivateKeyManager } from '@/utils/cryptoUtils';

const cookieOpts = {
    path: '/',                               // ← clé : dispo sur toutes les routes
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
    maxAge: 60 * 60 * 24 * 7,
} as const;

interface AuthState {
    authenticated: boolean
    loading: boolean
    user: AuthResponseData | null
    token: string | null
}

interface AuthResult {
    authenticated: boolean
    status?: number
    message?: string
}

interface EncryptedTokenPacket {
    key: string;   // base64 RSA-OAEP(AES_key)
    nonce: string; // base64 (12o)
    tag: string;   // base64 (16o)
    data: string;  // base64 (ciphertext sans tag)
}

interface AuthResponseBis {
    token: EncryptedTokenPacket;      // paquet chiffré
    data: AuthResponseData;           // utilisateur
    message?: string;
}

const PRIVATE_PEM = "-----BEGIN PRIVATE KEY-----\nMIIEvQIBADANBgkqhkiG9w0BAQEFAASCBKcwggSjAgEAAoIBAQC3tmYZQmttz4J55QyVkrnjJSG8IExhuaq+mBr7zG0Nkr/aXfA/spO+iRJH2+BvQH7JuiP2jSGFdPHnc6fCOVe8dP0FdkbrhziTBh8TBu/q21KoLbVLRoA7trrU7FUhpYvoXj2awEEIKwecoodm14CcPtS7I/M700JhdQQ2bohb/E5OKi42JH/6vkK5MfXDpvgqRJz8zyagAk0EwNJJxbUp51PnwdbeMVE0uL36n3zpzfyL3Ewcmby2f8XBTFfhEyT5uUWyB9HvLBhDEQLJ5AtEDRaCYKmUbuYRNkjydtfzDGi3qU/G5IDL/AolXRLjXhSW9/QWOtwnK1zR8MgvZldBAgMBAAECggEALJB6wmheCoiCsGKLc7Ig1RTCEvJrzO3+2RapPwkXMCp3zzcn6y+8zsTN8jAIiLxGtVUp2aphJhBZCQoBeSgzXAQuB5fqOsK1ytZ6jcFKTDixW5og5xZ+T13zguikexG/N1o7C/HUpLBZe5niiiQ/LsfAD3h4Tbwg12bNI44bFmLbirlkNE0K6o7iY4UgIlXk1B7953oQsEXcAYsVxIiezhlwBfXcxL1UkgJkuBGePZWg+gckHP3MaM76b6qkYf7coKB0pDpXj/AQlc/vqjia+tR28WQOZKqaPkBHNuvOKJpZeDP72hzkdqSm2Dd/L/YZ6NDOvAaoBM0A9HCriIGV9QKBgQDt4pud7DCyxBE6R3edf1iBp1SBSB61TtpwL+CJOF3xaa+ZBuWPa5YPwDYjh/n3uqOi39fXRVDRx1qxFspgQzUTfDWsUyVAk17GlVD19DdqRHM8x5tt73nwHWkweUeWMeLnqjEYP7MjaXBSF5hZE0NCAXFwq5iBl5uXtbTtBOjSPQKBgQDFs7w3bhAr45WHEi1TOzQxQrwH8KQOjWyRuaHvGWDJYRoaSivaGWgm8sL2hE5XmQD/PVDqLkMYFcHlG5jeatyQkxpKMZeC8i5WtXWIx72vy0V9tCsTRVJgC3Cb7ibal73FPVH0nGR0PQlGBe3MP1S6CVqsIJtbN7lRxBzXpAA9VQKBgF4jci/huzfTTXoBtD1Ymt7cKB3tO3HGnb0NxU6oRsz1ZO8rAodqLP1YtFQFSI+XhZA8Av+Gx7/so2A3Q6kqj4OBgQAL+079wuDVUC+1EIT2R6BC4ah0v42dJfZv2DuAzGmfOUR+WNbnr/JcsgZ2EAS763eKAJUwOPulhc7SRYyFAoGBAMOl0yWnrilA18ELo/8yH7yAJkRVSIV+P2sXIcHUg4vGpGZP8IWtjvy8UWRUGpNlvy7YJzMZBRzYq0FnxiiV6CDK8sFpwl67/E+i0+XAhV9q8iIsIlS3zYVuzMoDLEA22n5O6MQB4QeHP54kT+hI5DgW+bxNqHDk/GiGg2SD0BjhAoGAAhtwM7Oy6L5qnY9IhF4WtojkDejHWWFS9aaPkygKMOCqxQhnHYcoGNzv/85IdwGI82RjUzMO6udCJZKlSSevnOrIQ70FR46CPuZi61lmW2DrXxBRZ07QdHyx8OV2VKgfuHDTDAxbk+2sQ8VAtxoea/nOaMng/+G3mRw3/M72ksA=\n-----END PRIVATE KEY-----";


export const useAuthStore = defineStore('auth', {
    state: (): AuthState => ({
        authenticated: false,
        loading: false,
        user: null,
        token: null,
    }),

    getters: {
        // Informations utilisateur de base
        userId: (state) => state.user?.id,
        userEmail: (state) => state.user?.email,
        userLogin: (state) => state.user?.login,
        userSlug: (state) => state.user?.slug,
        userType: (state) => state.user?.user_type,

        // Statuts utilisateur
        isActive: (state) => state.user?.is_active ?? false,
        isArchived: (state) => state.user?.is_archive ?? false,
        isSuspended: (state) => state.user?.is_suspended ?? false,
        isDeleted: (state) => state.user?.is_deleted ?? false,

        // Groupes et permissions
        userGroups: (state): Group[] => state.user?.groups ?? [],
        userPermissions: (state): Permission[] => state.user?.user_permissions ?? [],

        // Vérification de permissions
        hasPermission: (state) => (codename: string): boolean => {
            if (!state.user) return false

            // Vérifier dans les permissions directes
            const directPermission = state.user.user_permissions.some(
                permission => permission.codename === codename
            )

            // Vérifier dans les permissions des groupes
            const groupPermission = state.user.groups.some(group =>
                group.permissions.some(permission => permission.codename === codename)
            )

            return directPermission || groupPermission
        },

        // Vérification de groupe
        hasGroup: (state) => (groupName: string): boolean => {
            if (!state.user) return false
            return state.user.groups.some(group => group.name === groupName)
        },

        // Token formaté pour les headers
        authToken: (state) => state.token ? `Bearer ${state.token}` : null,
    },

    actions: {
        async authenticateUser(payload: AuthData): Promise<AuthResult> {
            const { apiDjangoGuest } = useApi()
            this.loading = true

            try {
                // Normaliser l'identifiant: supprimer les espaces début/fin sans "dénaturer" le contenu.
                const normalizedPayload: AuthData = {
                    ...payload,
                    login: (payload.login ?? '').trim(),
                }

                // Forward the payload as-is. It may include an optional turnstile_token.
                const data: AuthResponse = await apiDjangoGuest('/auth/login/', {
                    method: 'POST',
                    body: normalizedPayload,
                })

                if (data && data.token) {
                    // Stocker le token original (pas de Bearer prefix)
                    this.token = data.token
                    this.user = data.data
                    this.authenticated = true
                    localStorage.setItem('auth_token', data.token);
                    localStorage.setItem('auth_user', JSON.stringify(data));

                    // Sauvegarder en cookie pour la persistance
                    // const tokenCookie = useCookie('token')
                    // const userCookie = useCookie('user')
                    const tokenCookie = useCookie<string | null>('token', cookieOpts)
                    const userCookie  = useCookie<string | null>('user',  cookieOpts)

                    tokenCookie.value = data.token
                    userCookie.value = JSON.stringify(data.data)

                    return { authenticated: true }
                }

                this.authenticated = false
                return {
                    authenticated: false,
                    message: data?.message ?? 'Une erreur s\'est produite',
                }
            } catch (error: any) {
                const status = error?.response?.status
                const response = error?.response?._data as any
                const message = response?.data.message ?? 'Une erreur s\'est produite'

                this.authenticated = false
                return { authenticated: false, status, message }
            } finally {
                this.loading = false
            }
        },

        // Initialiser le store depuis les cookies (pour la persistance entre sessions)
        // PATCH: Améliorer initializeFromCookies pour utiliser localStorage
        async initializeFromCookies() {
            const token = localStorage.getItem('auth_token')
            const user = localStorage.getItem('auth_user')


            const tokenCookie = useCookie<string | null>('token', cookieOpts)
            const userCookie  = useCookie<string | null>('user',  cookieOpts)

            tokenCookie.value = token
            userCookie.value = user


            // const tokenCookie = useCookie('token')
            // const userCookie = useCookie('user')

            // Essayer d'abord les cookies
            if (tokenCookie.value && userCookie.value) {
                try {
                    this.token = tokenCookie.value
                    this.user = JSON.parse(userCookie.value as string)

                    // Valider le token avant de marquer comme authentifié
                    const isValid = await this.validateToken()
                    if (isValid) {
                        this.authenticated = true
                        return
                    } else {
                        // Token invalide, nettoyer
                        this.logUserOut()
                        return
                    }
                } catch (error) {
                    this.logUserOut()
                }
            }

            // PATCH: Si les cookies échouent, essayer localStorage
            if (process.client) {
                try {
                    const token = localStorage.getItem('auth_token')
                    const user = localStorage.getItem('auth_user')

                    if (token && user) {
                        this.token = token
                        this.user = JSON.parse(user)

                        // Valider le token avant de marquer comme authentifié
                        const isValid = await this.validateToken()
                        if (isValid) {
                            this.authenticated = true

                            // Synchroniser avec les cookies
                            const tokenCookie = useCookie<string | null>('token', cookieOpts)
                            const userCookie = useCookie<string | null>('user', cookieOpts)
                            tokenCookie.value = token
                            userCookie.value = user
                        } else {
                            // Token invalide, nettoyer
                            this.logUserOut()
                        }
                    }
                } catch (error) {
                    this.logUserOut()
                }
            }
        },

        // Valider le token côté serveur
        async validateToken(): Promise<boolean> {
            if (!this.token) return false;

            try {
                const { apiDjangoAuth } = useApi();
                // Utiliser l'endpoint de profil pour valider le token
                // C'est plus standard et évite de créer un endpoint dédié
                await apiDjangoAuth('/auth/me/', {
                    method: 'GET'
                });
                return true;
            } catch (error: any) {
                // Si erreur 401, le token est invalide
                if (error?.status === 401 || error?.statusCode === 401) {
                    return false;
                }
                // Pour les autres erreurs, considérer le token comme valide pour éviter les déconnexions intempestives
                return true;
            }
        },

        logUserOut() {
            // console.trace('[auth.logout] called from:');
            const tokenCookie = useCookie('token')
            const userCookie = useCookie('user')

            this.authenticated = false
            this.user = null
            this.token = null

            tokenCookie.value = null
            userCookie.value = null

            // PATCH: Nettoyer aussi localStorage
            if (process.client) {
                localStorage.removeItem('auth_token')
                localStorage.removeItem('auth_user')
            }
        },

        async authenticateUserBis({ login, password }: AuthData): Promise<any> {
            const { apiDjangoGuest } = useApi();
            const { encryptLoginPayload } = useClientCrypto()

            this.loading = true;

            try {
                // 1) chiffrer login+mdp côté client
                const cipher = await encryptLoginPayload((login ?? '').trim(), password);
                const resp = await apiDjangoGuest('/auth/login/bis/', {
                    method: 'POST',
                    credentials: 'include',
                    body: { cipher },
                }) as { token: EncryptedTokenPacket; data: AuthResponseData; message?: string };

                // ⬇️ utiliser la clé collée dans le fichier
                const keyMgr = PrivateKeyManager.getInstance();
                if (!keyMgr.hasPrivateKey() && PRIVATE_PEM) {
                    keyMgr.setPrivateKey(PRIVATE_PEM);
                }

                const privatePem = (await keyMgr.ensurePrivateKey()) || PRIVATE_PEM;

                if (!privatePem || !privatePem.includes('BEGIN PRIVATE KEY')) {
                    return { authenticated: false, message: 'Clé privée indisponible (ou invalide).' };
                }

                // 1) plaintext (string)
                const plaintext = await decryptTokenPacketToString(resp.token, privatePem);

                // 2) normaliser -> récupérer access/refresh
                const { access, refresh } = extractAccessRefresh(plaintext);
                if (!access) {
                    return { authenticated: false, message: 'Token access introuvable dans la réponse déchiffrée.' };
                }

                // 3) MAJ état + persistance
                this.token = access;         // ← à utiliser dans Authorization
                this.user  = resp.data;
                this.authenticated = true;

                const tokenCookie = useCookie<string | null>('token', cookieOpts);
                const userCookie  = useCookie<string | null>('user',  cookieOpts);
                tokenCookie.value = access;
                userCookie.value  = JSON.stringify(resp.data);

                try {
                    localStorage.setItem('auth_token', access);
                    localStorage.setItem('auth_user', JSON.stringify(resp.data));
                    if (refresh) localStorage.setItem('refresh_token', refresh);
                } catch {}

                return { authenticated: true, user: this.user, token: this.token, message: resp.message };
            } catch (error: any) {
                const status   = error?.response?.status;
                const apiError = error?.response?._data as ApiErrorResponse;
                const message  = apiError?.message ?? 'Échec de connexion (flux chiffré).';
                this.authenticated = false;
                return { authenticated: false, status, message };
            } finally {
                this.loading = false;
            }
        },

        // Mettre à jour les données utilisateur
        updateUser(userData: Partial<AuthResponseData>) {
            if (this.user) {
                this.user = { ...this.user, ...userData }

                // Mettre à jour le cookie
                const userCookie = useCookie('user')
                userCookie.value = JSON.stringify(this.user)
            }
        },

        generateToken(user: AuthResponse): string {
            return `Bearer ${user.token}`
        },
    },
}) as unknown as StoreDefinition<'auth', AuthState, {}, {
    authenticateUser(data: AuthData): Promise<AuthResult>,
    authenticateUserBis(data: AuthData): Promise<AuthResult>,
    initializeFromCookies(): void,
    logUserOut(): void,
    updateUser(userData: Partial<AuthResponseData>): void,
    generateToken(user: AuthResponse): string
}>